<template>
  <lbs-dialog v-if="isVisible"
              :title="$t('visibility')"
              class="visibility-panel"
              @close="isVisible = false"
              initial-position="BB"
              key="us:visibility-panel"
  >
    <b-form-group slot="body">
      <template #label>
        <b-form-checkbox :checked="allSelected"
                         :indeterminate="someSelected"
                         @change="toggleAll"
        >
          {{ $t('all') }}
        </b-form-checkbox>
      </template>
      <template v-slot="{ ariaDescribedby }">
        <b-form-checkbox-group v-model="selectedOptions"
                               class="ml-4"
                               stacked
        >
          <b-form-checkbox v-for="option in displayOptions"
                           class="vis-checkbox"
                           :key="option.key"
                           :value="option.key"
                           :aria-describedby="ariaDescribedby"
          >
            <span>{{ option.label }}</span>
            <b-button v-if="option.addOn"
                      size="sm"
                      @click="$emit(option.addOn.action)"
            >
              {{ option.addOn.label }}
            </b-button>
          </b-form-checkbox>
        </b-form-checkbox-group>
      </template>
    </b-form-group>
    <div slot="footer">
      <b-button @click="showOptions"
                size="sm"
      >
        {{ $t('options') }}
      </b-button>
    </div>
  </lbs-dialog>
</template>

<script>
  import LbsDialog from '../../components/vue/lbs-dialog';
  import { without } from 'lodash';

  export default {
    name: 'VisibilityPanel',

    i18nOptions: { keyPrefix: 'uScope.extensions.settings-service.visibility-panel' },

    components: { LbsDialog },

    props: {
      userConfig: {
        type: Object,
        required: true
      },
      settingsPanel: {
        type: Object,
        required: true
      }
    },

    data () {
      return {
        isVisible: false,
        options: [],
        selectedOptions: []
      };
    },

    computed: {
      displayOptions () {
        return this.options.filter(Boolean); // remove nulls
      },
      someSelected () {
        return this.selectedOptions.length !== 0 && this.selectedOptions.length < this.displayOptions.length;
      },
      allSelected () {
        return this.selectedOptions.length === this.displayOptions.length;
      }
    },

    methods: {
      toggleVisibility () { // internal: show/hide this UI
        this.isVisible = !this.isVisible;
      },
      toggleAll (checked) { // private: checks/unchecks all options
        this.selectedOptions = checked ? this.displayOptions.map(({ key }) => key) : [];
      },
      addOption ({ key, label, addOn }, order) { // public: enables addition of new options
        if (this.options[order]) {
          return; // already added
        }
        this.options[order] = { key, label, addOn };
        this.updateOption(key);
      },
      refreshSettings () { // private: syncs all options to userConfig service
        this.selectedOptions.splice(0);
        this.options.forEach(({key}) => this.updateOption(key));
      },
      updateOption (key) { // private: syncs an individual option to the userConfig service
        const isOptionVisible = this.userConfig.get(key);
        if (isOptionVisible) {
          this.selectedOptions.push(key);
        }
      },
      showOptions () { // private: shows the options UI
        this.settingsPanel.show(true);
        this.toggleVisibility();
      }
    },

    watch: {
      selectedOptions (newValue, oldValue) {
        const addedOptions = without(newValue, ...oldValue).filter(Boolean);
        const removedOptions = without(oldValue, ...newValue).filter(Boolean);
        addedOptions.forEach((key) => this.userConfig.set(key, true));
        removedOptions.forEach((key) => this.userConfig.set(key, false));
      }
    },

    mounted () {
      this.userConfig.onLoad(this.refreshSettings);
    }
  };
</script>

<style scoped lang="scss">
.visibility-panel {
  min-width: 200px;

  >>> { // trying to minimize the size of the UI
    fieldset {
      margin-bottom: 0;
    }
    .custom-control {
      min-height: 1.2rem;
    }
  }

  >>> { // For some reason the checkboxes don't align with the labels in this control
    .custom-control-label::before,
    .custom-control-label::after {
      top: 0
    }
  }
}

.vis-checkbox >>> label { // prevent addons from wrapping
  display: flex;
  justify-content: space-between;
  min-width: 135px;
}
</style>
